import { useEffect, useMemo, useRef, useState } from "react";

/**
 * What became of a shown suggestion. Terminal outcomes survive to submit
 * because the server persists them as provenance.
 */
export const SuggestionOutcome = {
  Suggested: "suggested",
  Edited: "edited",
  Dismissed: "dismissed",
} as const;
export type SuggestionOutcome =
  (typeof SuggestionOutcome)[keyof typeof SuggestionOutcome];

export interface SuggestionOutcomeEntry<TSuggestion> {
  suggestion: TSuggestion;
  outcome: SuggestionOutcome;
}

/**
 * Generic state machine behind the app's applied-by-default AI-suggestion
 * pattern. See docs/ai-suggestion-ui.md for the full interaction contract.
 * Eligible suggestions are applied into the caller's field state the moment
 * they seed; each stays in "suggested" state until the user either edits the
 * field (value kept, state cleared) or dismisses it (field emptied).
 * Domain-agnostic over the suggestion payload — the caller supplies how to
 * key a suggestion, whether it's eligible, and how applying/dismissing one
 * affects its own field-value state.
 */
export interface UseAiSuggestionsInput<TSuggestion> {
  /** Raw suggestions from the data source, undefined while loading. */
  suggestions: TSuggestion[] | undefined;
  /** Stable identifier for a suggestion (e.g. the target field id). */
  getSuggestionKey: (suggestion: TSuggestion) => string;
  /**
   * Whether a suggestion should be applied at seed time — typically "does
   * the target field exist and is it currently empty". Suggestions that
   * fail this check are silently dropped, never surfaced.
   */
  isSuggestionEligible: (suggestion: TSuggestion) => boolean;
  /**
   * Write the suggestion's value into the caller's own field state. Called
   * at seed time for every eligible suggestion.
   */
  onApply: (suggestion: TSuggestion) => void;
  /**
   * Empty the suggestion's target field. Called on dismiss (per-field X or
   * Dismiss all) — never on edit, where the user's value must be kept.
   */
  onDismiss: (suggestion: TSuggestion) => void;
  /** Fired per seeded batch that applied at least one suggestion. */
  onSeeded?: (params: { applied: TSuggestion[]; totalCount: number }) => void;
}

export interface UseAiSuggestionsResult<TSuggestion> {
  /** Suggestions currently in "suggested" state: applied, not yet edited or dismissed. */
  appliedSuggestions: Record<string, TSuggestion>;
  appliedCount: number;
  /** Every suggestion ever shown, with what became of it. For submit-time provenance. */
  suggestionOutcomes: Record<string, SuggestionOutcomeEntry<TSuggestion>>;
  /** Count applied across all seeded batches. For submit-time analytics. */
  seededCount: number;
  /** Count of all suggestions returned by the source, including ones never seeded (already-filled fields). Used for submit-time analytics. */
  totalSuggestionCount: number;
  /** Empties the target field via onDismiss and drops the suggested state. Terminal. */
  dismissSuggestion: (key: string) => void;
  dismissAllSuggestions: () => void;
  /**
   * The user edited the field themselves: keep the value, drop the suggested
   * state. Never touches field state.
   */
  markSuggestionEdited: (key: string) => void;
  /**
   * System retraction (AI veto): empties the field and erases the entry as if
   * never shown — the user did nothing, so no outcome may be recorded, and a
   * value they later type must count as their own. Suggested-state only.
   */
  retractSuggestion: (key: string) => void;
  /** Clears all state and re-arms seeding, for reuse after a successful submit. */
  reset: () => void;
}

/**
 * Seeds each suggestion exactly once PER KEY, not once overall: autofill
 * arrives in two passes, and a later batch must fill the fields the first one
 * left alone without re-applying anything already shown.
 */
export function useAiSuggestions<TSuggestion>({
  suggestions,
  getSuggestionKey,
  isSuggestionEligible,
  onApply,
  onDismiss,
  onSeeded,
}: UseAiSuggestionsInput<TSuggestion>): UseAiSuggestionsResult<TSuggestion> {
  const [suggestionOutcomes, setSuggestionOutcomes] = useState<
    Record<string, SuggestionOutcomeEntry<TSuggestion>>
  >({});
  const [seededCount, setSeededCount] = useState(0);
  const [totalSuggestionCount, setTotalSuggestionCount] = useState(0);
  // reset() bumps this so the seed effect re-runs even when the caller's
  // `suggestions` array keeps the same identity (e.g. a cached query result
  // on a dialog that stays mounted across submissions).
  const [seedGeneration, setSeedGeneration] = useState(0);
  const seededKeysRef = useRef(new Set<string>());

  const getSuggestionKeyRef = useRef(getSuggestionKey);
  getSuggestionKeyRef.current = getSuggestionKey;
  const isSuggestionEligibleRef = useRef(isSuggestionEligible);
  isSuggestionEligibleRef.current = isSuggestionEligible;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const onSeededRef = useRef(onSeeded);
  onSeededRef.current = onSeeded;

  // onApply/onDismiss are caller-owned side effects (they update the caller's
  // own field state), so they must run outside state updaters rather than
  // inside them — React may invoke updaters more than once per commit.
  useEffect(() => {
    if (!suggestions || suggestions.length === 0) {
      return;
    }

    const unseeded = suggestions.filter(
      (suggestion) =>
        !seededKeysRef.current.has(getSuggestionKeyRef.current(suggestion)),
    );
    if (unseeded.length === 0) {
      return;
    }

    const applied: Record<string, SuggestionOutcomeEntry<TSuggestion>> = {};
    for (const suggestion of unseeded) {
      const key = getSuggestionKeyRef.current(suggestion);
      seededKeysRef.current.add(key);
      if (!isSuggestionEligibleRef.current(suggestion)) {
        continue;
      }
      onApplyRef.current(suggestion);
      applied[key] = { suggestion, outcome: SuggestionOutcome.Suggested };
    }

    // Counts include suggestions nothing was eligible for (every suggested
    // field already filled), so submit-time analytics keep their denominator.
    setTotalSuggestionCount((count) => count + unseeded.length);

    const appliedList = Object.values(applied);
    if (appliedList.length > 0) {
      setSuggestionOutcomes((prev) => ({ ...prev, ...applied }));
      setSeededCount((count) => count + appliedList.length);
      onSeededRef.current?.({
        applied: appliedList.map((entry) => entry.suggestion),
        totalCount: unseeded.length,
      });
    }
  }, [suggestions, seedGeneration]);

  const setOutcome = (key: string, outcome: SuggestionOutcome) => {
    setSuggestionOutcomes((prev) => {
      const entry = prev[key];
      if (!entry || entry.outcome === outcome) {
        return prev;
      }
      return { ...prev, [key]: { ...entry, outcome } };
    });
  };

  const appliedSuggestions = useMemo(() => {
    const applied: Record<string, TSuggestion> = {};
    for (const [key, entry] of Object.entries(suggestionOutcomes)) {
      if (entry.outcome === SuggestionOutcome.Suggested) {
        applied[key] = entry.suggestion;
      }
    }
    return applied;
  }, [suggestionOutcomes]);

  const dismissSuggestion = (key: string) => {
    const entry = suggestionOutcomes[key];
    if (!entry || entry.outcome !== SuggestionOutcome.Suggested) {
      return;
    }
    onDismissRef.current(entry.suggestion);
    setOutcome(key, SuggestionOutcome.Dismissed);
  };

  const dismissAllSuggestions = () => {
    for (const suggestion of Object.values(appliedSuggestions)) {
      onDismissRef.current(suggestion);
    }
    setSuggestionOutcomes((prev) => {
      const next = { ...prev };
      for (const [key, entry] of Object.entries(next)) {
        if (entry.outcome === SuggestionOutcome.Suggested) {
          next[key] = { ...entry, outcome: SuggestionOutcome.Dismissed };
        }
      }
      return next;
    });
  };

  const markSuggestionEdited = (key: string) => {
    setOutcome(key, SuggestionOutcome.Edited);
  };

  const retractSuggestion = (key: string) => {
    const entry = suggestionOutcomes[key];
    if (!entry || entry.outcome !== SuggestionOutcome.Suggested) {
      return;
    }
    onDismissRef.current(entry.suggestion);
    setSuggestionOutcomes((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
    // Take back the seed count so analytics don't report a vetoed value as
    // one the user dealt with.
    setSeededCount((count) => Math.max(0, count - 1));
  };

  const reset = () => {
    setSuggestionOutcomes({});
    setSeededCount(0);
    setTotalSuggestionCount(0);
    seededKeysRef.current = new Set();
    setSeedGeneration((generation) => generation + 1);
  };

  return {
    appliedSuggestions,
    appliedCount: Object.keys(appliedSuggestions).length,
    suggestionOutcomes,
    seededCount,
    totalSuggestionCount,
    dismissSuggestion,
    dismissAllSuggestions,
    markSuggestionEdited,
    retractSuggestion,
    reset,
  };
}
